import { NotFoundPage, RouteError } from "@wirevod/app-core";
import { createBrowserRouter, Navigate } from "react-router";
import { AppLayout } from "./AppLayout";
import { AuthLayout } from "./AuthLayout";
import { RedirectIfSignedIn, RequireSession } from "./guards";

/** Route-level code splitting: each page is its own chunk. */
const page =
  <M extends Record<string, unknown>>(load: () => Promise<M>, name: keyof M) =>
  async () => {
    const module = await load();
    return { Component: module[name] as React.ComponentType };
  };

export const router = createBrowserRouter([
  {
    errorElement: <RouteError />,
    children: [
      {
        element: <RedirectIfSignedIn />,
        children: [
          {
            element: <AuthLayout />,
            children: [
              { path: "login", lazy: page(() => import("@/features/auth/LoginPage"), "LoginPage") },
              {
                path: "signup",
                lazy: page(() => import("@/features/auth/SignupPage"), "SignupPage"),
              },
            ],
          },
        ],
      },
      {
        // Public: an invitee may be signed out, or signed in as someone else.
        element: <AuthLayout />,
        children: [
          {
            path: "invite",
            lazy: page(() => import("@/features/auth/InvitePage"), "InvitePage"),
          },
        ],
      },
      {
        element: <RequireSession />,
        children: [
          {
            element: <AuthLayout />,
            children: [
              {
                path: "change-password",
                lazy: page(
                  () => import("@/features/auth/ChangePasswordPage"),
                  "ChangePasswordPage",
                ),
              },
            ],
          },
          {
            element: <AppLayout />,
            errorElement: <RouteError />,
            children: [
              {
                index: true,
                lazy: page(() => import("@/features/overview/OverviewPage"), "OverviewPage"),
              },
              {
                path: "assets",
                lazy: page(() => import("@/features/assets/AssetsPage"), "AssetsPage"),
              },
              {
                path: "assets/:id",
                lazy: page(() => import("@/features/assets/AssetDetailPage"), "AssetDetailPage"),
              },
              {
                path: "api-keys",
                lazy: page(() => import("@/features/api-keys/ApiKeysPage"), "ApiKeysPage"),
              },
              {
                path: "settings",
                children: [
                  { index: true, element: <Navigate to="account" replace /> },
                  {
                    path: "account",
                    lazy: page(() => import("@/features/settings/AccountPage"), "AccountPage"),
                  },
                  {
                    path: "members",
                    lazy: page(() => import("@/features/members/MembersPage"), "MembersPage"),
                  },
                  {
                    path: "organization",
                    lazy: page(
                      () => import("@/features/settings/OrganizationPage"),
                      "OrganizationPage",
                    ),
                  },
                ],
              },
              { path: "*", element: <NotFoundPage /> },
            ],
          },
        ],
      },
    ],
  },
]);
